<script setup lang="ts">
// Sierra's .btn: 44 px, bold, in sentence case. Variants: default, primary, ghost and
// danger; `size="sm"` is Sierra's .small (36 px). `href` renders a link styled as a
// button, for full-page navigations such as login.
//
// `confirm` makes it two-tap (Sierra line 2773): the first tap arms it, red, reading
// "Tap again to confirm" (or the given text) for 3 s; a second tap within that time
// emits `click`. The armed state is announced through a polite live region in <body>.
import { onBeforeUnmount, ref } from 'vue';

const props = withDefaults(
  defineProps<{
    variant?: 'default' | 'primary' | 'ghost' | 'danger';
    size?: 'md' | 'sm';
    href?: string;
    type?: 'button' | 'submit';
    disabled?: boolean;
    confirm?: boolean | string;
  }>(),
  { variant: 'default', size: 'md', type: 'button', disabled: false, confirm: false },
);
const emit = defineEmits<{ click: [event: MouseEvent] }>();

const ARM_MS = 3000;
const armed = ref(false);
let timer: ReturnType<typeof setTimeout> | undefined;
const disarm = (): void => {
  armed.value = false;
  clearTimeout(timer);
};
onBeforeUnmount(disarm);

function onClick(event: MouseEvent): void {
  if (props.confirm === false || armed.value) {
    disarm();
    emit('click', event);
    return;
  }
  // The first tap of a two-tap button doesn't submit a form.
  event.preventDefault();
  armed.value = true;
  timer = setTimeout(disarm, ARM_MS);
}

const armedText = (): string => (typeof props.confirm === 'string' && props.confirm !== '' ? props.confirm : 'Tap again to confirm');
</script>

<template>
  <a v-if="href" :href="href" class="itc-btn" :class="[`itc-btn--${variant}`, { 'itc-btn--sm': size === 'sm' }]" @click="emit('click', $event)">
    <slot />
  </a>
  <button
    v-else
    class="itc-btn"
    :class="[`itc-btn--${variant}`, { 'itc-btn--sm': size === 'sm', 'itc-btn--armed': armed }]"
    :type="type"
    :disabled="disabled"
    @click="onClick"
    @blur="disarm"
  >
    <template v-if="armed">{{ armedText() }}</template>
    <slot v-else />
    <!-- Outside the button, so it isn't part of the button's name. -->
    <Teleport v-if="confirm !== false" to="body">
      <span class="itc-btn__live" aria-live="polite">{{ armed ? armedText() : '' }}</span>
    </Teleport>
  </button>
</template>

<style scoped>
.itc-btn__live {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
}
</style>
